import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Card } from "@/components/Shared";
import { formatINR, timeAgo } from "@/lib/format";
import { Clock, User, Pause, AlertTriangle, LayoutGrid, List, GripVertical, Inbox, UserCheck, FileEdit, Eye, Send, CheckCircle2, XCircle, Paperclip } from "lucide-react";
import ListView from "@/components/ListView";
import { NewEnquiryDialog, ENQUIRY_SOURCES } from "@/components/QuickCreate";
import { Plus } from "lucide-react";

export const COLUMNS = ["New", "Assigned", "Preparing Quotation", "Internal Review", "Quote Sent", "On Hold", "Converted To Order", "Lost"];

export const SLA_STYLE = {
  "On Track": { bar: "bg-[#0B7C5A]", chip: "bg-[#0B7C5A]/10 text-[#0B7C5A]" },
  "Due Soon": { bar: "bg-[#F59E0B]", chip: "bg-[#F59E0B]/15 text-[#B45309]" },
  Breached: { bar: "bg-[#F83A84]", chip: "bg-[#F83A84]/10 text-[#F83A84]" },
  Paused: { bar: "bg-[#6B7189]", chip: "bg-[#6B7189]/10 text-[#6B7189]" },
  Unassigned: { bar: "bg-[#101D51]", chip: "bg-[#101D51]/10 text-[#101D51]" },
};

export function SlaChip({ enq }) {
  const s = SLA_STYLE[enq.sla_status] || SLA_STYLE["On Track"];
  const rem = enq.clocks?.sla_remaining_minutes;
  let label = enq.sla_status;
  if (enq.sla_status === "Breached" && rem != null) label = `Breached ${Math.abs(rem)}m`;
  if (enq.sla_status === "Due Soon" && rem != null) label = `Due in ${rem}m`;
  if (enq.sla_status === "On Track" && rem != null && rem > 0) label = `${rem}m left`;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${s.chip}`} data-testid={`sla-${enq.number}`}>{label}</span>;
}

export const PRIORITY_STYLE = { Urgent: "text-[#F83A84]", High: "text-[#B45309]", Standard: "text-[#6B7189]" };

const LANE = {
  New: { color: "#3B82F6", Icon: Inbox }, Assigned: { color: "#8B5CF6", Icon: UserCheck }, "Preparing Quotation": { color: "#D97706", Icon: FileEdit },
  "Internal Review": { color: "#0891B2", Icon: Eye }, "Quote Sent": { color: "#0B7C5A", Icon: Send }, "On Hold": { color: "#6B7189", Icon: Pause },
  "Converted To Order": { color: "#16A34A", Icon: CheckCircle2 }, Lost: { color: "#F83A84", Icon: XCircle },
};
const initials = (n) => (n || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const PRIORITY_BADGE = { Urgent: "bg-[#F83A84]/10 text-[#F83A84]", High: "bg-[#FEF3C7] text-[#B45309]", Standard: "bg-[#F5F6FA] text-[#6B7189]" };

const ENQ_FIELDS = [
  { key: "status", label: "Status", type: "select", options: COLUMNS },
  { key: "priority", label: "Priority", type: "select", options: ["Urgent", "High", "Standard"] },
  { key: "source", label: "Source", type: "select", options: ENQUIRY_SOURCES },
  { key: "sla_status", label: "SLA Status", type: "select", options: ["On Track", "Due Soon", "Breached", "Paused", "Unassigned"] },
  { key: "customer_name", label: "Customer", type: "text" },
  { key: "assigned_name", label: "Owner", type: "text" },
  { key: "description", label: "Requirement", type: "text" },
  { key: "received_at", label: "Received date", type: "date" },
  { key: "last_activity_at", label: "Last WhatsApp Activity", type: "date" },
];

function EnquiryCard({ e, onClick, onDragStart, onDragEnd, dragging }) {
  const s = SLA_STYLE[e.sla_status] || SLA_STYLE["On Track"];
  const draggable = !["Converted To Order", "Lost"].includes(e.status);
  const value = e.order_value || e.quote_value;
  return (
    <div onClick={onClick} data-testid={`enq-card-${e.number}`} draggable={draggable}
      onDragStart={(ev) => { ev.dataTransfer.setData("text/plain", e.id); ev.dataTransfer.effectAllowed = "move"; onDragStart(e); }} onDragEnd={onDragEnd}
      className={`group relative bg-white rounded-xl border border-[#E6E8F0] shadow-[0_1px_2px_rgba(16,29,81,0.06)] transition-all duration-150 ${draggable ? "cursor-grab active:cursor-grabbing hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(16,29,81,0.12)] hover:border-[#C7CBDA]" : "cursor-pointer hover:shadow-md"} ${dragging ? "opacity-30 scale-[0.98] border-dashed" : ""}`}>
      <div className={`absolute left-0 top-3 bottom-3 w-1 rounded-r ${s.bar}`} />
      <div className="p-3 pl-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-mono text-[#6B7189] flex items-center gap-1">{draggable && <GripVertical className="w-3 h-3 text-[#C7CBDA] opacity-0 group-hover:opacity-100 transition-opacity" />}{e.number}</span>
          <SlaChip enq={e} />
        </div>
        <div className="font-semibold text-[#101D51] text-sm mt-1.5 truncate">{e.customer_name}</div>
        <div className="text-xs text-[#6B7189] mt-0.5 line-clamp-2 leading-relaxed">{e.description}</div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${PRIORITY_BADGE[e.priority] || PRIORITY_BADGE.Standard}`}>{e.priority}</span>
            {e.attachments_count > 0 && <span className="text-[10px] text-[#6B7189] flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{e.attachments_count}</span>}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[#6B7189]">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeAgo(e.received_at)}</span>
            {e.assigned_name
              ? <span title={e.assigned_name} className="w-6 h-6 rounded-full bg-[#101D51] text-white text-[10px] font-bold flex items-center justify-center">{initials(e.assigned_name)}</span>
              : <span className="w-6 h-6 rounded-full border border-dashed border-[#C7CBDA] flex items-center justify-center" title="Unassigned"><User className="w-3 h-3 text-[#C7CBDA]" /></span>}
          </div>
        </div>
        {value > 0 && <div className="mt-2 pt-2 border-t border-dashed border-[#F0F1F5] text-sm font-bold text-[#101D51] flex items-center justify-between"><span className="text-[10px] font-medium text-[#6B7189] uppercase tracking-wide">{e.order_value ? "Order" : "Quote"}</span>{formatINR(value)}</div>}
      </div>
    </div>
  );
}

export default function Enquiries() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("all");
  const [mode, setMode] = useState(() => localStorage.getItem("tradedesk.enq.mode") || "board");
  const setModeP = (m) => { setMode(m); localStorage.setItem("tradedesk.enq.mode", m); };
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const enters = useRef({});
  const [lostDlg, setLostDlg] = useState(null);
  const [lostReason, setLostReason] = useState("");
  const [newEnq, setNewEnq] = useState(false);
  const reload = () => api.get("/enquiries/board").then((r) => setData(r.data));
  const run = async (p, msg) => { try { await p; toast.success(msg); } catch (err) { toast.error(apiError(err)); } finally { reload(); } };

  const WORK = ["Assigned", "Preparing Quotation", "Internal Review"];
  // Which columns can a card be dropped into? Mirrors backend rules.
  const canDrop = (e, col) => {
    if (col === e.status) return false;
    if (["Converted To Order", "Lost"].includes(e.status)) return false;
    if (["New", "Quote Sent", "Converted To Order"].includes(col)) return false;
    if (col === "Lost") return true;
    if (col === "On Hold") return true;
    if (WORK.includes(col)) return true;
    return false;
  };
  const doMove = async (e, col) => {
    if (col === "Lost") { setLostReason(""); setLostDlg(e); return; }
    if (col === "On Hold") return run(api.post(`/enquiries/${e.id}/hold`, { reason: "Moved on board" }), `${e.number} put on hold`);
    const steps = async () => {
      if (e.status === "On Hold") await api.post(`/enquiries/${e.id}/resume`);
      if (!e.assigned_to) await api.post(`/enquiries/${e.id}/assign`, { user_id: user.id });
      const cur = (await api.get(`/enquiries/${e.id}`)).data.status;
      if (cur !== col) await api.post(`/enquiries/${e.id}/status`, { status: col });
    };
    return run(steps(), `${e.number} → ${col}${!e.assigned_to ? " (assigned to you)" : ""}`);
  };
  useEffect(() => {
    const load = () => api.get("/enquiries/board").then((r) => setData(r.data));
    load(); const t = setInterval(load, 30000); return () => clearInterval(t);
  }, []);
  if (!data) return <Loading />;

  const list = data.enquiries.filter((e) => tab === "all" ? true : tab === "unassigned" ? !e.assigned_to : e.assigned_to === tab);
  const open = list.filter((e) => !["Converted To Order", "Lost"].includes(e.status));
  const breached = open.filter((e) => e.sla_status === "Breached").length;
  const pipeline = open.reduce((s, e) => s + (e.quote_value || 0), 0);
  const tabs = [{ id: "all", name: "All" }, { id: user.id, name: "Mine" }, { id: "unassigned", name: "Unassigned" },
    ...data.users.filter((u) => u.id !== user.id).map((u) => ({ id: u.id, name: u.name.split(" ")[0] }))];

  const modeToggle = (
    <div className="flex rounded-lg border border-[#E6E8F0] overflow-hidden" data-testid="enq-mode-toggle">
      {[["board", LayoutGrid, "Board"], ["list", List, "List"]].map(([m, Icon, l]) => (
        <button key={m} onClick={() => setModeP(m)} data-testid={`enq-mode-${m}`} className={`h-9 px-3 text-xs font-medium flex items-center gap-1 ${mode === m ? "bg-[#101D51] text-white" : "bg-white text-[#6B7189]"}`}><Icon className="w-3.5 h-3.5" /> {l}</button>))}
    </div>);
  const headerActions = (
    <div className="flex gap-2 items-center">
      {modeToggle}
      <Button className="h-9 bg-[#0B7C5A] hover:bg-[#09664a]" onClick={() => setNewEnq(true)} data-testid="new-enquiry-btn"><Plus className="w-4 h-4" /> New enquiry</Button>
    </div>);
  const newEnqDialog = newEnq && <NewEnquiryDialog onClose={() => { setNewEnq(false); reload(); }} />;

  if (mode === "list") {
    const rows = data.enquiries.map((e) => ({ ...e, path: `/enquiries/${e.id}` }));
    return (
      <div>
        <PageHeader title="Enquiries" subtitle={`${open.length} open · ${breached} breached · pipeline ${formatINR(pipeline)}`} actions={headerActions} />
        {newEnqDialog}
        <ListView object="enquiries" title="Enquiries" rows={rows} me={user.id} statuses={COLUMNS} defaultSort={{ key: "received_at", dir: "desc" }} onDataChange={reload} fields={ENQ_FIELDS} owner={user.is_super_admin || user.role === "Owner"}
          searchKeys={["number", "customer_name", "description", "assigned_name", "source"]}
          views={[
            { id: "all", name: "All Enquiries", filter: () => true },
            { id: "open", name: "All Open", filter: (e) => !["Converted To Order", "Lost"].includes(e.status) },
            { id: "mine", name: "My Open Enquiries", filter: (e, me) => e.assigned_to === me && !["Converted To Order", "Lost"].includes(e.status) },
            { id: "unassigned", name: "Unassigned", filter: (e) => !e.assigned_to && e.status === "New" },
            { id: "breached", name: "SLA Breached", filter: (e) => e.sla_status === "Breached" },
            { id: "due-soon", name: "Due Soon", filter: (e) => e.sla_status === "Due Soon" },
            { id: "quote-sent", name: "Quote Sent · Awaiting", filter: (e) => e.status === "Quote Sent" },
            { id: "hold", name: "On Hold", filter: (e) => e.status === "On Hold" },
            { id: "won", name: "Converted To Order", filter: (e) => e.status === "Converted To Order" },
            { id: "lost", name: "Lost", filter: (e) => e.status === "Lost" },
            ...ENQUIRY_SOURCES.map((s) => ({ id: `src-${s}`, name: `Source · ${s}`, filter: (e) => e.source === s })),
          ]}
          columns={[
            { key: "number", label: "Enquiry" }, { key: "customer_name", label: "Customer" },
            { key: "status", label: "Status", render: (e) => <span className="text-xs px-2 py-0.5 rounded-full bg-[#101D51] text-white">{e.status}</span> },
            { key: "source", label: "Source", render: (e) => <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F6FA] text-[#6B7189]">{e.source || "—"}</span> },
            { key: "sla_status", label: "SLA", render: (e) => <SlaChip enq={e} /> },
            { key: "priority", label: "Priority", render: (e) => <span className={`font-medium ${PRIORITY_STYLE[e.priority]}`}>{e.priority}</span> },
            { key: "assigned_name", label: "Owner", render: (e) => e.assigned_name || "—" },
            { key: "received_at", label: "Received", render: (e) => timeAgo(e.received_at) },
            { key: "last_activity_at", label: "Last Activity", render: (e) => (e.last_activity_at ? timeAgo(e.last_activity_at) : "—") },
            { key: "quote_value", label: "Quote value", right: true, sum: true, render: (e) => (e.quote_value ? formatINR(e.quote_value) : "—") },
            { key: "description", label: "Requirement", render: (e) => <span className="text-[#6B7189] truncate inline-block max-w-[260px]">{e.description}</span> },
          ]} />
      </div>);
  }

  return (
    <div>
      <PageHeader title="Enquiries" subtitle={`${open.length} open · ${breached} breached · pipeline ${formatINR(pipeline)}`} actions={headerActions} />
      {newEnqDialog}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-3 -mx-1 px-1">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`enq-tab-${t.name.toLowerCase()}`}
            className={`px-3 h-9 rounded-full text-sm whitespace-nowrap border transition-colors ${tab === t.id ? "bg-[#101D51] text-white border-[#101D51]" : "bg-white text-[#6B7189] border-[#E6E8F0] hover:border-[#101D51]"}`}>
            {t.name}
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-[#EEF0F6] border border-[#E1E4EE] p-3 -mx-3 sm:-mx-5 lg:-mx-6 sm:rounded-none sm:border-x-0" style={{ backgroundImage: "radial-gradient(#D5D9E6 0.8px, transparent 0.8px)", backgroundSize: "18px 18px" }}>
      <div className="sm:hidden text-[11px] text-[#6B7189] text-center mb-2" data-testid="board-swipe-hint">Swipe sideways to move between stages</div>
      <div className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory sm:snap-proximity items-stretch min-h-[calc(100vh-330px)] no-scrollbar sm:[scrollbar-width:auto]" data-testid="enquiry-board">
        {COLUMNS.map((col) => {
          const items = list.filter((e) => e.status === col);
          const total = items.reduce((s, e) => s + (e.order_value || e.quote_value || 0), 0);
          const allowed = drag ? canDrop(drag, col) : null;
          const isOver = over === col;
          const L = LANE[col];
          const laneCls = drag ? (allowed ? (isOver ? "ring-2 ring-[#0B7C5A] bg-[#0B7C5A]/10" : "ring-2 ring-dashed ring-[#0B7C5A]/40 bg-white/70") : "opacity-40 bg-white/50") : "bg-white/70";
          return (
            <section key={col} className={`w-[84vw] sm:w-72 shrink-0 snap-center sm:snap-start flex flex-col rounded-xl transition-all duration-150 ${laneCls}`} data-testid={`enq-col-${col.replace(/\s+/g, "-").toLowerCase()}`}
              onDragEnter={(ev) => { ev.preventDefault(); enters.current[col] = (enters.current[col] || 0) + 1; setOver(col); }}
              onDragOver={(ev) => { if (allowed) { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; } }}
              onDragLeave={() => { enters.current[col] = (enters.current[col] || 1) - 1; if (enters.current[col] <= 0) { enters.current[col] = 0; setOver((o) => (o === col ? null : o)); } }}
              onDrop={(ev) => { ev.preventDefault(); enters.current = {}; setOver(null); if (drag && allowed) doMove(drag, col); else if (drag) toast.error(`Can't move to ${col} by dragging`); setDrag(null); }}>
              <header className="px-3 pt-3 pb-2 border-b border-[#E6E8F0] rounded-t-xl" style={{ borderTop: `3px solid ${L.color}` }}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-wide text-[#101D51] flex items-center gap-1.5"><L.Icon className="w-3.5 h-3.5" style={{ color: L.color }} />{col}</div>
                  <span className="min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center text-white" style={{ background: L.color }}>{items.length}</span>
                </div>
                <div className="text-[11px] text-[#6B7189] mt-0.5 h-4">{total > 0 ? formatINR(total) : items.length ? `${items.length} enquir${items.length > 1 ? "ies" : "y"}` : "Empty"}</div>
              </header>
              <div className="flex-1 space-y-2.5 p-2.5 overflow-y-auto max-h-[calc(100vh-380px)]" data-testid={`enq-drop-${col.replace(/\s+/g, "-").toLowerCase()}`}>
                {items.map((e) => <EnquiryCard key={e.id} e={e} dragging={drag?.id === e.id} onDragStart={setDrag} onDragEnd={() => { setDrag(null); setOver(null); enters.current = {}; }} onClick={() => navigate(`/enquiries/${e.id}`)} />)}
                {drag && allowed && isOver && drag.status !== col && <div className="h-20 rounded-xl border-2 border-dashed border-[#0B7C5A]/60 bg-[#0B7C5A]/5 flex items-center justify-center text-xs font-medium text-[#0B7C5A]">Drop to move to {col}</div>}
                {items.length === 0 && !(drag && allowed && isOver) && <div className="h-24 rounded-xl border border-dashed border-[#D5D9E6] flex items-center justify-center text-xs text-[#9AA0B5]">{drag && allowed ? "Drop here" : "No enquiries"}</div>}
              </div>
            </section>
          );
        })}
      </div>
      </div>
      {breached > 0 && <div className="flex items-center gap-2 text-xs text-[#F83A84]" data-testid="breached-note"><AlertTriangle className="w-3.5 h-3.5" /> {breached} enquiry(ies) past the 2-hour quote SLA</div>}
      <Dialog open={!!lostDlg} onOpenChange={(o) => !o && setLostDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Mark {lostDlg?.number} as lost</DialogTitle></DialogHeader>
          <Textarea data-testid="drag-lost-reason" rows={3} value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Reason (required)…" />
          <DialogFooter><Button className="bg-[#F83A84] hover:bg-[#d62f70]" disabled={!lostReason.trim()} data-testid="drag-lost-submit"
            onClick={() => run(api.post(`/enquiries/${lostDlg.id}/lost`, { reason: lostReason }), `${lostDlg.number} marked lost`).then(() => setLostDlg(null))}>Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
